import React, { FC, useContext, useEffect, useRef, useState } from 'react'
import { navigate } from '@components/ui/link'
import config from '@utils/config'
import { AppStateContext } from '@components/layouts/DynamicLayout'
import { joinClasses, makeClasses } from '@utils/styles'
import { Colors } from '@utils/css-variables'
import { pushEvent } from '@utils/tracking'
import { subscribeAuthState, type AuthStatus } from '@utils/authResolver'

type Classes = {
	root: string
	header: string
	loader: string
	loaderContainer: string
	placeholder: string
}

const classes: Classes = makeClasses({
	root: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		maxWidth: '100%',
		height: '100vh'
	},
	header: {
		width: '100%',
		background: Colors.primary,
		height: '150px'
	},
	loaderContainer: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		justifyContent: 'center',
		alignItems: 'center'
	},
	loader: {
		border: `4px solid ${Colors.lightGrey}`,
		borderTop: `4px solid ${Colors.blue}`,
		borderRadius: '50%',
		width: '26px',
		height: '26px',
		margin: '15px',
		animation: 'spin 2s linear infinite'
	},
	placeholder: {
		height: '100px'
	}
})

const AUTH_REDIRECT_RETRY_KEY = 'authRedirectRetryCount'
const AUTH_REDIRECT_MAX_RETRIES = 2
const AUTH_REDIRECT_ACCOUNT_TIMEOUT_MS = 15000

const LoginRedirect: FC = () => {
	const [authStatus, setAuthStatus] = useState<AuthStatus>('idle')
	const { setAuthUser, pageData } = useContext(AppStateContext)
	// Holds the navigation timer so it can be cancelled if the component
	// unmounts before the 50ms elapses (avoids navigate() on unmounted component).
	const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const handledRef = useRef(false)
	const lastHandledAccountIdRef = useRef<string | null>(null)

	const resetHandledState = () => {
		handledRef.current = false
		lastHandledAccountIdRef.current = null
	}

	const authRedirect: string = config.localStorage.authRedirect
	const redirectLinkRef = useRef<string | null>(
		localStorage.getItem(config.localStorage.authRedirect)
	)

	// B2C signup flow uses a dedicated policy. We compare it with the
	// policy present in idTokenClaims to decide sign_up vs login.
	const signupPolicy =
		process.env.AUTHORITY_CREATE_ACCOUNT?.split('/').pop()?.toLowerCase() || ''

	const getRetryCount = (): number => {
		const value = Number(sessionStorage.getItem(AUTH_REDIRECT_RETRY_KEY) || '0')
		return Number.isNaN(value) ? 0 : value
	}

	const incrementRetryCount = (): number => {
		const nextValue = getRetryCount() + 1
		sessionStorage.setItem(AUTH_REDIRECT_RETRY_KEY, String(nextValue))
		return nextValue
	}

	const clearRetryCount = () => {
		sessionStorage.removeItem(AUTH_REDIRECT_RETRY_KEY)
	}

	const getStoredRedirectTarget = (): string => {
		const storedTarget =
			redirectLinkRef.current || localStorage.getItem(authRedirect)

		if (storedTarget) {
			redirectLinkRef.current = storedTarget
		}

		return storedTarget || config.homepage.baseURL
	}

	const consumeStoredRedirectTarget = () => {
		redirectLinkRef.current = null
		localStorage.removeItem(authRedirect)
	}

	const trackAuthSuccess = (accountInfo: any) => {
		// Depending on the executed B2C policy, this auth success is either
		// a real account creation (sign_up) or a standard authentication (login).
		const policy = String(
			accountInfo?.idTokenClaims?.tfp || accountInfo?.idTokenClaims?.acr || ''
		).toLowerCase()
		const isSignup = signupPolicy !== '' && policy === signupPolicy
		const eventName = isSignup ? 'sign_up' : 'login'
		// React effects/callbacks can run more than once in a session. Keep one
		// analytics event per account/policy/event combination.
		const dedupeKey = `tracking:${eventName}:${
			accountInfo?.homeAccountId || 'unknown'
		}:${policy || 'none'}`

		if (sessionStorage.getItem(dedupeKey)) {
			return
		}

		pushEvent(eventName, {
			account_type: 'citoyen',
			method: 'email'
		})

		sessionStorage.setItem(dedupeKey, '1')
	}

	useEffect(() => {
		let isInitialSnapshot = true

		const unsubscribe = subscribeAuthState(({ status, account, user }) => {
			setAuthStatus(status)

			console.log(performance.now(), `[LoginRedirect] Auth state changed: status=${status}`)

			if (status === 'resolved' && user && account) {
				const accountId = account.homeAccountId
				const isSameHandledAccount =
					handledRef.current && lastHandledAccountIdRef.current === accountId

				if (isSameHandledAccount) {
					return
				}

				handledRef.current = true
				lastHandledAccountIdRef.current = accountId

				const redirectTarget = getStoredRedirectTarget()
				setAuthUser(user)
				trackAuthSuccess(account)
				clearRetryCount()

				console.log(
					performance.now(),
					`[LoginRedirect] BEFORE Navigating to stored redirect target: ${redirectTarget}`
				)
				navTimerRef.current = setTimeout(() => {
					console.log(
						performance.now(),
						`[LoginRedirect] Navigating to stored redirect target: ${redirectTarget}`
					)
					navigate(redirectTarget)
					consumeStoredRedirectTarget()
				}, 200)
				return
			}

			if (status === 'profile_incomplete') {
				resetHandledState()
				setAuthUser(null)
				clearRetryCount()
				navigate(config.auth.baseUrl)
				return
			}

			if (status === 'error') {
				resetHandledState()
				setAuthUser(null)
				clearRetryCount()
				navigate(config.auth.baseUrl)
				return
			}

			if (status === 'unauthenticated' && !isInitialSnapshot) {
				resetHandledState()
				const retryCount = incrementRetryCount()
				setAuthUser(null)
				if (retryCount > AUTH_REDIRECT_MAX_RETRIES) {
					clearRetryCount()
					navigate(config.homepage.baseURL)
					consumeStoredRedirectTarget()
					return
				}
				navigate(config.auth.baseUrl)
			}
		})

		isInitialSnapshot = false

		return () => {
			unsubscribe()
			if (navTimerRef.current) {
				clearTimeout(navTimerRef.current)
			}
		}
	}, [setAuthUser, authRedirect])

	useEffect(() => {
		console.log(performance.now(), `[LoginRedirect] Auth status effect triggered: ${authStatus}`)
		const currentStatus = authStatus
		if (
			currentStatus === 'resolved' ||
			currentStatus === 'profile_incomplete'
		) {
			return
		}

		const timeoutId = setTimeout(() => {
			setAuthUser(null)
			clearRetryCount()
			navigate(config.homepage.baseURL)
			consumeStoredRedirectTarget()
		}, AUTH_REDIRECT_ACCOUNT_TIMEOUT_MS)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [authStatus, setAuthUser, authRedirect])

	return (
		<div className={classes.root}>
			<div className={classes.header}></div>
			<div className={classes.loaderContainer}>
				<div className={joinClasses([classes.loader, 'spin'])} />
				<span style={{ fontSize: '1rem' }}>{pageData?.assets?.loading}</span>
			</div>
			<div className={classes.placeholder}></div>
		</div>
	)
}

export default LoginRedirect
